import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

export type NavigationProvider = 'google' | 'apple' | 'ask';

interface NavigationOptions {
  latitude: number;
  longitude: number;
  title?: string;
  address?: string;
}

interface NavigationHookReturn {
  navigate: (options: NavigationOptions) => void;
  provider: NavigationProvider;
  setProvider: (provider: NavigationProvider) => void;
  isModalOpen: boolean;
  setIsModalOpen: (open: boolean) => void;
  pendingNavigation: NavigationOptions | null;
  executeNavigation: (options: NavigationOptions, selectedProvider: NavigationProvider) => void;
}

// Detect if user is on iOS/macOS
const isAppleDevice = () => {
  return /iPad|iPhone|iPod|Mac/.test(navigator.userAgent);
};

// Check if Apple Maps is available (iOS/macOS)
const isAppleMapsAvailable = () => {
  return isAppleDevice();
};

export const useNavigation = (): NavigationHookReturn => {
  const { t } = useTranslation();
  
  // Get saved preference from localStorage or default based on platform
  const getDefaultProvider = (): NavigationProvider => {
    const saved = localStorage.getItem('navigation-provider') as NavigationProvider;
    if (saved && ['google', 'apple', 'ask'].includes(saved)) {
      return saved;
    }
    // Default to 'ask' so users can choose
    return 'ask';
  };

  const [provider, setProviderState] = useState<NavigationProvider>(getDefaultProvider);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState<NavigationOptions | null>(null);

  const setProvider = useCallback((newProvider: NavigationProvider) => {
    setProviderState(newProvider);
    localStorage.setItem('navigation-provider', newProvider);
  }, []);

  const openGoogleMaps = useCallback((options: NavigationOptions) => {
    const { latitude, longitude, title, address } = options;
    
    // Create query string - prefer title and address over coordinates for better UX
    let query = '';
    if (title && address) {
      query = encodeURIComponent(`${title}, ${address}`);
    } else if (title) {
      query = encodeURIComponent(title);
    } else if (address) {
      query = encodeURIComponent(address);
    } else {
      query = `${latitude},${longitude}`;
    }

    // Use Google Maps directions URL for better navigation experience
    const url = `https://www.google.com/maps/dir/?api=1&destination=${query}`;
    window.open(url, '_blank');
  }, []);

  const openAppleMaps = useCallback((options: NavigationOptions) => {
    const { latitude, longitude, title } = options;
    
    // Apple Maps URL scheme
    let url = `http://maps.apple.com/?daddr=${latitude},${longitude}`;
    
    if (title) {
      url += `&q=${encodeURIComponent(title)}`;
    }
    
    // On iOS, try to use the native maps:// scheme first
    if (isAppleDevice()) {
      const nativeUrl = `maps://?daddr=${latitude},${longitude}`;
      window.location.href = nativeUrl;
      
      // Fallback to web version if native app doesn't open
      setTimeout(() => {
        window.open(url, '_blank');
      }, 500);
    } else {
      // On non-Apple devices, open web version
      window.open(url, '_blank');
    }
  }, []);

  const executeNavigation = useCallback((options: NavigationOptions, selectedProvider: NavigationProvider) => {
    switch (selectedProvider) {
      case 'google':
        openGoogleMaps(options);
        break;
      case 'apple':
        if (isAppleMapsAvailable()) {
          openAppleMaps(options);
        } else {
          // Fallback to Google Maps if Apple Maps isn't available
          console.warn('Apple Maps not available on this device, falling back to Google Maps');
          openGoogleMaps(options);
        }
        break;
      default:
        console.error('Invalid navigation provider:', selectedProvider);
        openGoogleMaps(options); // Fallback to Google Maps
    }
  }, [openGoogleMaps, openAppleMaps]);

  const navigate = useCallback((options: NavigationOptions) => {
    // Validate coordinates
    if (!options.latitude || !options.longitude) {
      console.error('Invalid coordinates provided for navigation');
      return;
    }

    if (provider === 'ask') {
      // Show modal to let user choose
      setPendingNavigation(options);
      setIsModalOpen(true);
    } else {
      // Use saved preference
      executeNavigation(options, provider);
    }
  }, [provider, executeNavigation]);

  return {
    navigate,
    provider,
    setProvider,
    isModalOpen,
    setIsModalOpen,
    pendingNavigation,
    executeNavigation
  };
};

// Helper function to handle navigation from modal choice
export const useNavigationModalHandler = () => {
  const navigation = useNavigation();

  const handleProviderChoice = useCallback((selectedProvider: NavigationProvider, rememberChoice = false) => {
    if (!navigation.pendingNavigation) return;

    // Execute the pending navigation using the main hook's logic
    navigation.executeNavigation(navigation.pendingNavigation, selectedProvider);

    // Clean up
    navigation.setIsModalOpen(false);
  }, [navigation]);

  return {
    ...navigation,
    handleProviderChoice
  };
}; 